import React, { useState } from 'react';
import {
  UserPlus,
  X,
  Calendar,
  Clock,
  MapPin,
  Save,
  CheckCircle2
} from 'lucide-react';
import { PracticeClient } from '../../types/practiceManagement';
import { Language } from '../../types/kundali';
import { createPracticeClient } from '../../lib/practiceManagementService';

interface NewClientModalProps {
  lang: Language;
  onClose: () => void;
  onClientCreated: (client: PracticeClient) => void;
}

export const NewClientModal: React.FC<NewClientModalProps> = ({
  lang,
  onClose,
  onClientCreated
}) => {
  const isHi = lang === 'hi';
  const [name, setName] = useState('');
  const [dob, setDob] = useState('1992-06-15');
  const [tob, setTob] = useState('08:30');
  const [pob, setPob] = useState('New Delhi, India');
  const [gender, setGender] = useState<'MALE' | 'FEMALE' | 'OTHER'>('MALE');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    // Calculate approximate age
    const birthYear = new Date(dob).getFullYear();
    const currentYear = new Date().getFullYear();
    const age = Math.max(1, currentYear - birthYear);

    const newClient = createPracticeClient({
      name: name.trim(),
      dateOfBirth: dob,
      timeOfBirth: tob,
      placeOfBirth: pob,
      gender,
      email,
      phone,
      notes,
      ageYears: age,
      lifeStage: age < 25 ? 'Student & Foundation' : age < 55 ? 'Active Career & Family' : 'Maturity & Reflection',
      lagnaSign: 'Mithuna (Gemini)',
      moonSign: 'Vrishabha (Taurus)',
      nakshatra: 'Rohini (Pada 2)',
      activeDasha: 'Jupiter / Saturn',
      status: 'ACTIVE',
      tags: ['New Intake', 'Individual Consultation'],
      hasUpcomingTimingAlert: false
    });

    onClientCreated(newClient);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-astro-primary/60 backdrop-blur-xs">
      <div className="bg-white dark:bg-slate-850 w-full max-w-lg rounded-2xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="p-4 bg-astro-secondary-surface dark:bg-astro-primary border-b border-astro-border dark:border-astro-strong-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-amber-500" />
            <h3 className="text-sm font-black text-astro-text dark:text-astro-muted">
              {isHi ? 'नया क्लाइंट पंजीकरण (Client Intake)' : 'New Client Registration'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-astro-muted hover:text-astro-secondary-text dark:hover:text-astro-muted cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-3.5 text-xs">
          <div className="space-y-1">
            <label className="font-bold text-astro-secondary-text dark:text-astro-muted">
              Full Legal Name *:
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Vikramaditya Rathore"
              className="w-full p-2.5 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted font-bold focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="font-bold text-astro-secondary-text dark:text-astro-muted">
                Date of Birth *:
              </label>
              <input
                type="date"
                required
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                className="w-full p-2 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
              />
            </div>

            <div className="space-y-1">
              <label className="font-bold text-astro-secondary-text dark:text-astro-muted">
                Time of Birth *:
              </label>
              <input
                type="time"
                required
                value={tob}
                onChange={(e) => setTob(e.target.value)}
                className="w-full p-2 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="font-bold text-astro-secondary-text dark:text-astro-muted">
              Place of Birth (City, Country) *:
            </label>
            <input
              type="text"
              required
              value={pob}
              onChange={(e) => setPob(e.target.value)}
              placeholder="e.g. Varanasi, UP, India"
              className="w-full p-2 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="font-bold text-astro-secondary-text dark:text-astro-muted">Phone:</label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98765 43210"
                className="w-full p-2 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
              />
            </div>
            <div className="space-y-1">
              <label className="font-bold text-astro-secondary-text dark:text-astro-muted">Email:</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="client@email.com"
                className="w-full p-2 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="font-bold text-astro-secondary-text dark:text-astro-muted">
              Initial Inquiries & Notes:
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Primary consultation context..."
              className="w-full p-2 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
            />
          </div>

          <div className="p-3 bg-astro-secondary-surface dark:bg-astro-primary border-t border-astro-border dark:border-astro-strong-border flex items-center justify-end gap-2 -mx-5 -mb-5 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-1.5 rounded-lg bg-astro-card dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted font-bold text-xs cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-1.5 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white font-bold text-xs flex items-center gap-1.5 shadow-xs cursor-pointer"
            >
              <Save className="w-3.5 h-3.5" />
              <span>Register Client</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
